import React from 'react'
import PropTypes from 'prop-types'
import { Box, Button, CircularProgress, LinearProgress, Stack, Tooltip, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../../../../generic/hooks/useSmartTranslation'
import { BLOCK_STATUS } from '../../hooks/useJobDetail'

// Historia 160 — piezas de presentación que comparten las pestañas del detalle del job (mocks 1-7):
// la tarjeta con rótulo en versalitas, el texto con elipsis y tooltip, la barra y el estado de carga
// o error de un bloque.

export const SECTION_SX = {
  bgcolor: 'background.card',
  borderRadius: 2,
  border: 1,
  borderColor: 'divider',
  overflow: 'hidden'
}

export const OVERLINE_SX = {
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  fontSize: '0.78rem',
  lineHeight: 1.4
}

/** Tarjeta con rótulo, como "CANTIDADES" o "MATERIALES DEL JOB". */
export const DetailSection = ({ title = '', action = null, children, sx = {}, bodySx = {}, testId = '' }) => (
  <Box sx={{ ...SECTION_SX, ...sx }} data-testid={testId || undefined} component='section' aria-label={title}>
    <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
      <Typography component='h3' sx={{ ...OVERLINE_SX, color: 'text.primary' }}>{title}</Typography>
      {action}
    </Stack>
    <Box sx={bodySx}>{children}</Box>
  </Box>
)

DetailSection.propTypes = {
  title: PropTypes.string,
  action: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.object,
  bodySx: PropTypes.object,
  testId: PropTypes.string
}

/**
 * RN17/RN18/D23: una línea; si no cabe, elipsis, y el tooltip lleva el texto entero. El tooltip existe
 * siempre que haya texto: jsdom no mide, y en planta sobra más que falta.
 */
export const EllipsisText = ({ text = '', sx = {}, testId = '' }) => (
  <Tooltip title={text} disableInteractive>
    <Typography
      component='span'
      data-testid={testId || undefined}
      sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, ...sx }}
    >
      {text}
    </Typography>
  </Tooltip>
)

EllipsisText.propTypes = { text: PropTypes.string, sx: PropTypes.object, testId: PropTypes.string }

/** Barra de avance acotada a 0..100. */
export const ProgressBar = ({ value = 0, color = 'primary', sx = {}, label = '' }) => (
  <LinearProgress
    variant='determinate'
    value={Math.max(0, Math.min(100, value))}
    color={color}
    aria-label={label || undefined}
    aria-valuenow={Math.round(Math.max(0, Math.min(100, value)))}
    sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', ...sx }}
  />
)

ProgressBar.propTypes = { value: PropTypes.number, color: PropTypes.string, sx: PropTypes.object, label: PropTypes.string }

/**
 * E4/D12: un bloque que carga o que ha fallado. El fallo se queda DENTRO de la pestaña, con
 * Reintentar; nada de diálogos (S44).
 */
export const BlockState = ({ status = BLOCK_STATUS.IDLE, onRetry = () => {}, children }) => {
  const { t_ } = useSmartTranslation()
  if (status === BLOCK_STATUS.FAILED) {
    return (
      <Stack alignItems='center' justifyContent='center' spacing={2} sx={{ minHeight: 200 }} role='alert'>
        <Typography color='error'>{t_('This block could not be loaded')}</Typography>
        <Button variant='outlined' onClick={onRetry} sx={{ minHeight: 48, minWidth: 48 }}>{t_('Retry')}</Button>
      </Stack>
    )
  }
  if (status === BLOCK_STATUS.LOADING || status === BLOCK_STATUS.IDLE) {
    return (
      <Stack alignItems='center' justifyContent='center' sx={{ minHeight: 200 }}>
        <CircularProgress aria-label={t_('Loading')} />
      </Stack>
    )
  }
  return children
}

BlockState.propTypes = { status: PropTypes.string, onRetry: PropTypes.func, children: PropTypes.node }

/** Chip pequeño en versalitas con color semántico (PENDIENTE, HECHA, ALTA…). Story 204: moved to the shared looks of SystemValueChip. */
export { OverlineLook as Pill } from '../../../../../../generic/systemValues/looks'

/** Cabecera de columna de tabla, como "CÓDIGO" o "REQUERIDO". */
export const HeaderCell = ({ children, align = 'left', sx = {} }) => (
  <Typography component='span' role='columnheader' sx={{ ...OVERLINE_SX, textAlign: align, display: 'block', ...sx }}>
    {children}
  </Typography>
)

HeaderCell.propTypes = { children: PropTypes.node, align: PropTypes.string, sx: PropTypes.object }
